import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { chosenMembers, matchMembers, type PickableMember } from './memberSearch';

/* ══ ICONS ═══════════════════════════════════════════════════════════
   Stroked, 20px grid, one consistent weight. Never emoji. */

const s = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const Icon = {
  grid: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <rect x="2.5" y="2.5" width="6.5" height="8" rx="1.2" />
      <rect x="11" y="2.5" width="6.5" height="5" rx="1.2" />
      <rect x="2.5" y="12.5" width="6.5" height="5" rx="1.2" />
      <rect x="11" y="9.5" width="6.5" height="8" rx="1.2" />
    </svg>
  ),
  calendar: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <rect x="2.5" y="4" width="15" height="13.5" rx="2" />
      <path d="M2.5 8.2h15M6.6 2.5v3M13.4 2.5v3" />
    </svg>
  ),
  people: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <circle cx="8" cy="7" r="3" />
      <path d="M2.5 17.2c0-3 2.5-5.2 5.5-5.2s5.5 2.2 5.5 5.2" />
      <path d="M14.2 5.3a2.8 2.8 0 0 1 0 5.3M17.5 16.8c0-2.1-.7-3.8-1.8-4.8" />
    </svg>
  ),
  wallet: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <rect x="2" y="5" width="16" height="11" rx="2" />
      <path d="M2 9h16M5.2 12.8h3" />
    </svg>
  ),
  book: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M3 4.8A1.8 1.8 0 0 1 4.8 3H9.2v14H4.8A1.8 1.8 0 0 1 3 15.2z" />
      <path d="M17 4.8A1.8 1.8 0 0 0 15.2 3H10.8v14h4.4A1.8 1.8 0 0 0 17 15.2z" />
    </svg>
  ),
  check2: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M3 5.6l2 2 3.6-3.6M3 13.6l2 2 3.6-3.6M11.4 6h5.6M11.4 14h5.6" />
    </svg>
  ),
  trash: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M4 5.5h12M8 5.5V3.6h4v1.9M6.1 5.5l.8 11h6.2l.8-11" />
    </svg>
  ),
  out: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M12.5 6V4.2A1.2 1.2 0 0 0 11.3 3H4.2A1.2 1.2 0 0 0 3 4.2v11.6A1.2 1.2 0 0 0 4.2 17h7.1a1.2 1.2 0 0 0 1.2-1.2V14" />
      <path d="M7.5 10h9.5M14.4 7.2 17.2 10l-2.8 2.8" />
    </svg>
  ),
  chev: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M6 8l4 4 4-4" />
    </svg>
  ),
  dice: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <rect x="3" y="3" width="14" height="14" rx="3" />
      <circle cx="7.2" cy="7.2" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.8" cy="12.8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.8" cy="7.2" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="7.2" cy="12.8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  ),
  box: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M2.6 6.2 10 2.8l7.4 3.4v7.6L10 17.2 2.6 13.8z" />
      <path d="M2.6 6.2 10 9.6l7.4-3.4M10 9.6v7.6" />
    </svg>
  ),
  pencil: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M13.6 3.4a1.9 1.9 0 0 1 2.7 2.7L7.2 15.2l-3.5.8.8-3.5z" />
      <path d="M12.2 4.8l3 3" />
    </svg>
  ),
  save: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M4 10.4l4 4 8-8.8" />
    </svg>
  ),
  cancel: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />
    </svg>
  ),
  megaphone: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M3 8.2v3.6a1.4 1.4 0 0 0 1.4 1.4h1.7L13 16.8V3.2L6.1 6.8H4.4A1.4 1.4 0 0 0 3 8.2z" />
      <path d="M15.8 7.4a3.4 3.4 0 0 1 0 5.2M6.1 13.2v2.6a1.2 1.2 0 0 0 1.2 1.2h.6a1.2 1.2 0 0 0 1.2-1.2v-1.5" />
    </svg>
  ),
  download: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <path d="M10 3v9.2M6.4 8.8 10 12.4l3.6-3.6M3.5 14.4v1.4a1.2 1.2 0 0 0 1.2 1.2h10.6a1.2 1.2 0 0 0 1.2-1.2v-1.4" />
    </svg>
  ),
  picture: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <rect x="2.5" y="3.8" width="15" height="12.4" rx="2" />
      <circle cx="7.2" cy="8" r="1.4" />
      <path d="M2.9 13.8 7 10.4l3.4 2.8 2.6-2.2 4.1 3.2" />
    </svg>
  ),
  search: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <circle cx="8.8" cy="8.8" r="5.3" />
      <path d="M12.8 12.8 17 17" />
    </svg>
  ),
  copy: () => (
    <svg viewBox="0 0 20 20" {...s}>
      <rect x="6.6" y="6.6" width="10.4" height="10.4" rx="2" />
      <path d="M13.4 6.6V5A1.9 1.9 0 0 0 11.5 3H5A1.9 1.9 0 0 0 3 5v6.5A1.9 1.9 0 0 0 5 13.4h1.6" />
    </svg>
  ),
  tick: () => (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10.4l4 4 8-8.8" />
    </svg>
  ),
};

/* ══ SIGIL — the halo mark from the reference ════════════════════════ */

export function Sigil({ size = 38 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 38 38" fill="none" aria-hidden="true">
      <circle cx="19" cy="19" r="17" stroke="#8C6B28" strokeWidth="1" />
      <circle cx="19" cy="19" r="12" stroke="#E8B84B" strokeWidth="1.5" />
      <path d="M19 9.5 L25.5 19 L19 28.5 L12.5 19 Z" fill="#E8B84B" fillOpacity="0.92" />
    </svg>
  );
}

/** The big decorative halo used on the art panel. */
export function HaloRing() {
  return (
    <svg className="halo-ring" viewBox="0 0 340 340" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id="haloGlow" cx="50%" cy="50%" r="50%">
          <stop offset="54%" stopColor="#E8B84B" stopOpacity="0" />
          <stop offset="84%" stopColor="#E8B84B" stopOpacity="0.34" />
          <stop offset="100%" stopColor="#E8B84B" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="170" cy="170" r="156" fill="url(#haloGlow)" />
      <circle cx="170" cy="170" r="122" stroke="#E8B84B" strokeWidth="1.6" strokeOpacity="0.6" />
      <circle cx="170" cy="170" r="99" stroke="#E8B84B" strokeWidth="1" strokeOpacity="0.26" />
      <circle cx="170" cy="170" r="138" stroke="#E8B84B" strokeWidth="1" strokeOpacity="0.15" strokeDasharray="3 10" />
      <g stroke="#E8B84B" strokeOpacity="0.36" strokeWidth="1">
        <path d="M170 34v22M170 284v22M34 170h22M284 170h22" />
      </g>
      <path d="M170 110 L212 170 L170 230 L128 170 Z" stroke="#E8B84B" strokeWidth="1.4" strokeOpacity="0.66" />
    </svg>
  );
}

/* ══ PROGRESS RING ═══════════════════════════════════════════════════ */

export function Ring({ value, target, label }: { value: number; target: number; label: string }) {
  const pct = target > 0 ? Math.min(1, value / target) : 0;
  const R = 46;
  const C = 2 * Math.PI * R;
  const done = pct >= 1;
  const shown = Math.round(value * 10) / 10;

  return (
    <div className="ringbox">
      <div className="ring">
        <svg width="108" height="108" viewBox="0 0 108 108">
          <circle className="trk" cx="54" cy="54" r={R} fill="none" strokeWidth="7" />
          <circle
            className={`fil${done ? ' done' : ''}`}
            cx="54"
            cy="54"
            r={R}
            fill="none"
            strokeWidth="7"
            strokeDasharray={C.toFixed(1)}
            strokeDashoffset={(C * (1 - pct)).toFixed(1)}
          />
        </svg>
        <span className="mid">
          <b>{shown}</b>
          <i>af {target}</i>
        </span>
      </div>
      <span className="cap">{label}</span>
    </div>
  );
}

/* ══ SPARKLINE ═══════════════════════════════════════════════════════ */

export function Sparkline({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const W = 300;
  const H = 56;
  const max = Math.max(...points, 1);
  const step = W / (points.length - 1);
  const xy = points.map((p, i) => [i * step, H - (p / max) * (H - 8) - 4] as const);
  const line = xy.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${line} L${W} ${H} L0 ${H} Z`;
  const last = xy[xy.length - 1];

  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Mæting síðustu viðburða">
      <defs>
        <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E8B84B" stopOpacity="0.26" />
          <stop offset="100%" stopColor="#E8B84B" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path className="ar" d={area} />
      <path className="ln" d={line} vectorEffect="non-scaling-stroke" />
      {last && <circle className="pt" cx={last[0]} cy={last[1]} r="3" />}
    </svg>
  );
}

/* ══ SMALL PIECES ════════════════════════════════════════════════════ */

export function Stat({ label, value, gold }: { label: string; value: ReactNode; gold?: boolean }) {
  return (
    <div className="stat">
      <span className="lbl">{label}</span>
      <b className={gold ? 'gold' : undefined}>{value}</b>
    </div>
  );
}

export function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

export function DeleteButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" className="ico del" aria-label={label} onClick={onClick}>
      <Icon.trash />
    </button>
  );
}

export function IconButton({
  onClick,
  label,
  tone,
  children,
}: {
  onClick: () => void;
  label: string;
  tone?: 'ok' | 'del';
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`ico${tone ? ` ${tone}` : ''}`}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/* ══ BALANCE CHART ═══════════════════════════════════════════════════
   Running balance over time. One measure, one hue, one axis — a line
   with an area wash, a hairline grid, a labelled endpoint, and a
   crosshair on hover. The ledger table below it is the table twin, so
   no value is reachable only by hovering. */


const W = 720;
const H = 232;
const PAD = { t: 18, r: 16, b: 26, l: 62 };

function niceStep(span: number): number {
  const raw = span / 3;
  const mag = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
}

const shortKr = (n: number): string => {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${(n / 1_000_000).toFixed(a >= 10_000_000 ? 0 : 1)}m`;
  if (a >= 1_000) return `${(n / 1_000).toFixed(a >= 10_000 ? 0 : 1)}k`;
  return String(Math.round(n));
};

export function BalanceChart({
  series,
}: {
  series: { date: string; balance: number; delta: number }[];
}) {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  if (series.length < 2) return null;

  const values = series.map((p) => p.balance);
  const lo = Math.min(0, ...values);
  const hi = Math.max(...values, 1);
  const step = niceStep(hi - lo);
  const yMin = Math.floor(lo / step) * step;
  const yMax = Math.ceil(hi / step) * step;

  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const x = (i: number) => PAD.l + (i / (series.length - 1)) * innerW;
  const y = (v: number) => PAD.t + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;

  const pts = series.map((p, i) => [x(i), y(p.balance)] as const);
  const line = pts.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)}`).join(' ');
  const base = y(Math.max(yMin, 0));
  const area = `${line} L${pts[pts.length - 1]![0].toFixed(1)} ${base} L${pts[0]![0].toFixed(1)} ${base} Z`;

  const ticks: number[] = [];
  for (let v = yMin; v <= yMax + 0.001; v += step) ticks.push(v);

  const last = series[series.length - 1]!;
  const active = hover === null ? null : series[hover];
  const activePt = hover === null ? null : pts[hover];

  function onMove(e: React.PointerEvent<SVGSVGElement>) {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const px = ((e.clientX - box.left) / box.width) * W;
    const i = Math.round(((px - PAD.l) / innerW) * (series.length - 1));
    setHover(Math.max(0, Math.min(series.length - 1, i)));
  }

  return (
    <div className="chart">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="chart-svg"
        role="img"
        aria-label={`Staða sjóðs yfir tíma, nú ${last.balance} krónur`}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="balFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8B84B" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#E8B84B" stopOpacity="0" />
          </linearGradient>
        </defs>

        {ticks.map((v) => (
          <g key={v}>
            <line
              className={v === 0 ? 'grid zero' : 'grid'}
              x1={PAD.l}
              x2={W - PAD.r}
              y1={y(v)}
              y2={y(v)}
            />
            <text className="tick" x={PAD.l - 10} y={y(v) + 3.5} textAnchor="end">
              {shortKr(v)}
            </text>
          </g>
        ))}

        <path className="bal-area" d={area} />
        <path className="bal-line" d={line} vectorEffect="non-scaling-stroke" />

        {activePt && (
          <g>
            <line className="cross" x1={activePt[0]} x2={activePt[0]} y1={PAD.t} y2={PAD.t + innerH} />
            <circle className="dot-hi" cx={activePt[0]} cy={activePt[1]} r="4.5" />
          </g>
        )}

        <circle className="dot-end" cx={pts[pts.length - 1]![0]} cy={pts[pts.length - 1]![1]} r="4" />

        <text className="xtick" x={PAD.l} y={H - 8}>
          {series[0]!.date}
        </text>
        <text className="xtick" x={W - PAD.r} y={H - 8} textAnchor="end">
          {last.date}
        </text>
      </svg>

      {active && activePt && (
        <div
          className="chart-tip"
          style={{
            left: `${(activePt[0] / W) * 100}%`,
            transform: activePt[0] > W * 0.62 ? 'translate(-100%, 0)' : 'translate(-6px, 0)',
          }}
        >
          <span className="lbl">{active.date}</span>
          <b>{active.balance.toLocaleString('is-IS')} kr.</b>
          <span className={active.delta >= 0 ? 'd up' : 'd down'}>
            {active.delta >= 0 ? '+' : '−'}
            {Math.abs(active.delta).toLocaleString('is-IS')} kr. þennan dag
          </span>
        </div>
      )}
    </div>
  );
}

/* ══ ATTENDANCE BARS ═════════════════════════════════════════════════
   Turnout per event. A bar chart rather than a line, because at this
   scale each evening is a thing that happened, not a sample of a
   continuous signal — and one bar is meaningful where one point is not.
   The dashed rule is the Gate A target of ten. */

export function AttendanceBars({
  data,
  target = 10,
}: {
  data: { date: string; n: number }[];
  target?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  if (data.length === 0) return null;

  const W = 320;
  const H = 150;
  const PAD = { t: 14, r: 6, b: 22, l: 6 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const max = Math.max(target, ...data.map((d) => d.n)) * 1.12;
  const slot = innerW / data.length;
  const bw = Math.min(38, Math.max(8, slot * 0.62));
  const y = (v: number) => PAD.t + innerH - (v / max) * innerH;

  const short = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString('is-IS', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="bars">
      {/* The target is named in a legend rather than on the rule itself.
          Inside the plot the caption sat just above the line, which is
          exactly where a bar lands once attendance reaches the goal —
          so the label collided with the very bars it existed for. */}
      <span className="bars-key">
        <i aria-hidden="true" />
        markmið {target}
      </span>
      <svg viewBox={`0 0 ${W} ${H}`} className="bars-svg" role="img"
           aria-label={`Mæting á ${data.length} viðburðum, markmið ${target}`}>
        <line className="bar-target" x1={PAD.l} x2={W - PAD.r} y1={y(target)} y2={y(target)} />

        {data.map((d, i) => {
          const x = PAD.l + slot * i + (slot - bw) / 2;
          const top = y(d.n);
          const on = hover === i;
          return (
            <g key={d.date + i} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
              <rect x={PAD.l + slot * i} y={PAD.t} width={slot} height={innerH} fill="transparent" />
              <rect
                className={`bar${on ? ' on' : ''}`}
                x={x}
                y={top}
                width={bw}
                height={Math.max(2, PAD.t + innerH - top)}
                rx="3"
              />
              <text className="bar-n" x={x + bw / 2} y={top - 5} textAnchor="middle">
                {d.n}
              </text>
              {data.length <= 8 && (
                <text className="bar-x" x={x + bw / 2} y={H - 6} textAnchor="middle">
                  {short(d.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ══ MEMBER PICKER ═══════════════════════════════════════════════════
   Type a name, pick it, move on. Replaces a wall of toggle chips that
   was ordered by attendance — fine for five players, unusable once the
   roll runs past a screenful, because the eye has no alphabet to
   navigate by.

   The list is always alphabetical (Icelandic collation), matching is
   accent-blind so "armann" finds "Ármann", and everything works from
   the keyboard: type, arrow, Enter. */

export type { PickableMember };

export function MemberPicker({
  members,
  selected,
  onChange,
  placeholder = 'Skrifaðu nafn…',
  emptyText = 'Engir spilarar skráðir enn.',
}: {
  members: PickableMember[];
  selected: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  emptyText?: string;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const chosen = chosenMembers(members, selected);
  const options = matchMembers(members, selected, query);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  /* Close when focus or a click goes elsewhere. */
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('focusin', away);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('focusin', away);
    };
  }, [open]);

  function add(id: number) {
    onChange([...selected, id]);
    setQuery('');
    setOpen(true);
    inputRef.current?.focus();
  }

  function remove(id: number) {
    onChange(selected.filter((x) => x !== id));
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setCursor((c) => {
        const n = options.length;
        if (n === 0) return 0;
        return e.key === 'ArrowDown' ? (c + 1) % n : (c - 1 + n) % n;
      });
      return;
    }
    if (e.key === 'Enter') {
      // Never let a pick submit the form around it.
      e.preventDefault();
      const hit = options[cursor];
      if (open && hit) add(hit.id);
      else setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    // Backspace on an empty box takes back the last person added.
    if (e.key === 'Backspace' && query === '' && selected.length) {
      remove(selected[selected.length - 1]!);
    }
  }

  if (members.length === 0) {
    return <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{emptyText}</span>;
  }

  return (
    <div className="mp" ref={boxRef}>
      {chosen.length > 0 && (
        <ul className="mp-chips">
          {chosen.map((m) => (
            <li key={m.id}>
              <span className="mp-chip">
                {m.name}
                <button
                  type="button"
                  aria-label={`Fjarlægja ${m.name}`}
                  onClick={() => remove(m.id)}
                >
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                    <path d="M6 6l8 8M14 6l-8 8" />
                  </svg>
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mp-field">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && options[cursor] ? `${listId}-${options[cursor]!.id}` : undefined}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
        />
        <span className="mp-caret" aria-hidden="true">
          <Icon.chev />
        </span>
      </div>

      {open && (
        <ul className="mp-list" id={listId} role="listbox">
          {options.length === 0 ? (
            <li className="mp-none">
              {query.trim() ? `Enginn spilari fannst fyrir „${query.trim()}“` : 'Allir valdir'}
            </li>
          ) : (
            options.map((m, i) => (
              <li key={m.id}>
                <button
                  type="button"
                  id={`${listId}-${m.id}`}
                  role="option"
                  aria-selected={i === cursor}
                  className={`mp-opt${i === cursor ? ' on' : ''}`}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => add(m.id)}
                >
                  <span>{m.name}</span>
                  {m.attendance !== undefined && m.attendance > 0 && (
                    <i>{m.attendance} kvöld</i>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
